import { openDB, type IDBPDatabase } from 'idb';
import type { TokenStore } from '../../api/client.ts';
import { recordDataEntry, recordBehaviourEvent, recordAbcEntry, recordBetweenSessionAbcEntry, type AbcEntryFields } from '../../api/aba.ts';
import { isSendable, isAbandoned, isPermanentRefusal, isDrainableBy, queueCounts, removalDecision } from './drain-scope.ts';
import { ApiError } from '../../api/errors.ts';
import { failureMessage } from '../error-labels.ts';

/**
 * Offline-first session queue (spec §10 lines 1536–1551, Scope D).
 *
 * ABA data entries, behaviour events and ABC entries are written to IndexedDB IMMEDIATELY and never block on the
 * network (line 1538). Each entry carries a client-generated idempotency key
 * minted BEFORE it could ever go offline (line 1550); the server dedupes a
 * replay to the same row, so re-syncing is always safe (the PR 9
 * offline-replay-is-idempotent gate). Sync is FOREGROUND ONLY - no Background
 * Sync API (line 1540, iOS Safari) - draining when connectivity returns while
 * the app is open, plus a manual "Sync now" (line 1549).
 *
 * This module is a singleton store: it owns the IndexedDB handle, an in-memory
 * pending/errored count for synchronous React reads (useSyncExternalStore), and
 * the drain loop. Optimistic UI (lines 1561–1564): the local write is the
 * user's confirmation; the server reconciles silently.
 *
 * Failure model (2026-07-06 P0-A): an entry whose sync failed is
 * marked status='error' but is NEVER invisible and NEVER abandoned - errored
 * entries stay in the unsynced count, are retried by every subsequent drain
 * (safe: idempotency key), and block session completion until they either sync
 * or the user deletes them. Before this fix, errored entries dropped out of the
 * pending count and no code path ever retried them - data silently stranded.
 */

const DB_NAME = 'hapmetrix-offline';
const STORE = 'pending-data-entries';

interface PendingEntryBase {
  /** Primary key === the idempotency key sent to the server (minted once). */
  idempotencyKey: string;
  /** The user who recorded the entry. Drains are scoped to the CURRENT user so
   *  a shared device never syncs user A's queued entries under user B's token
   *  (the server stamps recorded_by from the actor - wrong provenance).
   *  Optional for entries queued before this field existed. */
  userId?: string;
  /** Client capture time (epoch ms) - sent as clientTs. */
  clientTs: number;
  /** 'pending' = not yet acknowledged; 'inflight' = POST in progress (dequeueEntry
   *  refuses it); 'error' = last attempt failed (still queued - retried each drain);
   *  'rejected' = refused permanently: kept and shown, NEVER replayed. */
  status: 'pending' | 'inflight' | 'error' | 'rejected';
  /** Why a 'rejected' entry was refused — shown to the person who recorded it. */
  rejectedReason?: string;
  /** Set by `purgeEncounter` when a POST was already out; the drain then drops it. */
  abandoned?: boolean;
  /** Human label for optimistic display in the session log. */
  label: string;
}

export interface PendingDataEntry extends PendingEntryBase {
  /** Discriminant. Absent on entries queued before behaviour events joined. */
  kind?: 'data';
  encounterId: string;
  targetId: string;
  /** Optional sub-target this entry records against (parent target by default). */
  subTargetId?: string;
  dataType: string;
  valueJson: string;
}

export interface PendingBehaviourEvent extends PendingEntryBase {
  kind: 'behaviour';
  encounterId: string;
  targetId: string;
  count?: number;
  durationSeconds?: number;
  latencySeconds?: number;
  intensity?: string;
}

/** An ABC entry: in a session (encounterId) or between sessions (caseId only). `clientTs` is
 *  when the behaviour happened. */
export interface PendingAbcEntry extends PendingEntryBase {
  kind: 'abc';
  encounterId?: string;
  caseId: string;
  fields: AbcEntryFields;
}

export type PendingEntry = PendingDataEntry | PendingBehaviourEvent | PendingAbcEntry;

/** Result of one drain pass. `remaining` is every entry still queued afterwards
 *  (pending or errored) - callers gate on it instead of a stale React count. */
export interface DrainResult {
  synced: number;
  remaining: PendingEntry[];
}

let dbPromise: Promise<IDBPDatabase> | null = null;
function db(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, 1, {
      upgrade(d) {
        if (!d.objectStoreNames.contains(STORE)) {
          d.createObjectStore(STORE, { keyPath: 'idempotencyKey' });
        }
      },
    });
  }
  return dbPromise;
}

// ── Reactive snapshot (synchronous for useSyncExternalStore) ──────────────────

const listeners = new Set<() => void>();
let snapshot: {
  pending: number;
  errored: number;
  /** Refused for good: kept and shown, never replayed, and OUT of `pending`. */
  rejected: number;
  lastError: string | null;
  inflightKeys: readonly string[];
} = {
  pending: 0,
  errored: 0,
  rejected: 0,
  lastError: null,
  inflightKeys: [],
};

function setSnapshot(next: Partial<typeof snapshot>): void {
  snapshot = { ...snapshot, ...next };
  for (const l of listeners) l();
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSnapshot(): typeof snapshot {
  return snapshot;
}

/** Who the counts are for: on a shared device, another person's entries are theirs to see. */
let viewerUserId: string | undefined;
export async function setQueueViewer(userId: string | undefined): Promise<void> {
  viewerUserId = userId;
  await refreshCount();
}

async function refreshCount(): Promise<void> {
  setSnapshot(queueCounts((await (await db()).getAll(STORE)) as PendingEntry[], viewerUserId));
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `entry-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Enqueue a data entry (instant, offline-safe). Returns its idempotency key. */
export async function enqueueDataEntry(
  entry: Omit<PendingDataEntry, 'status' | 'idempotencyKey'> & { idempotencyKey?: string },
): Promise<string> {
  const idempotencyKey = entry.idempotencyKey ?? newIdempotencyKey();
  const record: PendingDataEntry = { ...entry, idempotencyKey, status: 'pending' };
  await (await db()).put(STORE, record);
  await refreshCount();
  return idempotencyKey;
}

export async function enqueueBehaviourEvent(
  entry: Omit<PendingBehaviourEvent, 'status' | 'kind' | 'idempotencyKey'> & { idempotencyKey?: string },
): Promise<string> {
  const idempotencyKey = entry.idempotencyKey ?? newIdempotencyKey();
  const record: PendingBehaviourEvent = { ...entry, idempotencyKey, kind: 'behaviour', status: 'pending' };
  await (await db()).put(STORE, record);
  await refreshCount();
  return idempotencyKey;
}

export async function enqueueAbcEntry(
  entry: Omit<PendingAbcEntry, 'status' | 'kind' | 'idempotencyKey'> & { idempotencyKey?: string },
): Promise<string> {
  const idempotencyKey = entry.idempotencyKey ?? newIdempotencyKey();
  const record: PendingAbcEntry = { ...entry, idempotencyKey, kind: 'abc', status: 'pending' };
  await (await db()).put(STORE, record);
  await refreshCount();
  return idempotencyKey;
}

async function allEntries(): Promise<PendingEntry[]> {
  return (await (await db()).getAll(STORE)) as PendingEntry[];
}

export async function listEntries(userId: string | undefined): Promise<PendingEntry[]> {
  return (await allEntries()).filter((e) => isDrainableBy(e, userId));
}

async function sendEntry(e: PendingEntry, store: TokenStore): Promise<void> {
  if (e.kind === 'abc') {
    const body = { ...e.fields, occurredAt: e.clientTs, idempotencyKey: e.idempotencyKey };
    if (e.encounterId !== undefined) await recordAbcEntry(e.encounterId, body, store);
    else await recordBetweenSessionAbcEntry(e.caseId, body, store);
    return;
  }
  if (e.kind === 'behaviour') {
    await recordBehaviourEvent(
      {
        behaviourTargetId: e.targetId,
        occurredAt: e.clientTs,
        ...(e.count !== undefined ? { count: e.count } : {}),
        ...(e.durationSeconds !== undefined ? { durationSeconds: e.durationSeconds } : {}),
        ...(e.latencySeconds !== undefined ? { latencySeconds: e.latencySeconds } : {}),
        ...(e.intensity !== undefined ? { intensity: e.intensity } : {}),
        clientTs: e.clientTs,
        idempotencyKey: e.idempotencyKey,
        encounterId: e.encounterId,
      },
      store,
    );
    return;
  }
  await recordDataEntry(
    e.encounterId,
    {
      targetId: e.targetId,
      ...(e.subTargetId ? { subTargetId: e.subTargetId } : {}),
      dataType: e.dataType,
      valueJson: e.valueJson,
      clientTs: e.clientTs,
      idempotencyKey: e.idempotencyKey,
    },
    store,
  );
}

/** Mark inflight iff the entry still exists (one tx) - a deleted entry must not be resurrected or sent. False when gone. */
async function markInflight(idempotencyKey: string): Promise<boolean> {
  const tx = (await db()).transaction(STORE, 'readwrite');
  const cur = (await tx.store.get(idempotencyKey)) as PendingEntry | undefined;
  if (cur !== undefined) await tx.store.put({ ...cur, status: 'inflight' as const });
  await tx.done;
  return cur !== undefined;
}

/**
 * One drain pass: replay EVERY queued entry (pending AND previously-errored -
 * the idempotency key makes replays safe, so a transient failure self-heals on
 * the next pass). On success the local record is removed; on failure it is kept
 * with status='error' - the entry is never lost and never leaves the unsynced
 * count.
 *
 * `userId` scopes the pass to that user's entries (legacy entries without a
 * userId are included). Another user's entries are skipped - never sent under
 * the wrong token - but still count as `remaining`.
 */
async function drainPass(store: TokenStore, userId?: string): Promise<DrainResult> {
  const all = await allEntries();
  const mine = all.filter((e) => isSendable(e, userId));
  let synced = 0;
  for (const e of mine) {
    if (!(await markInflight(e.idempotencyKey))) continue;
    await refreshCount();
    try {
      await sendEntry(e, store);
      await (await db()).delete(STORE, e.idempotencyKey);
      synced += 1;
    } catch (err) {
      // Keep the entry (data is never lost) - but only if it still exists: a deleted entry must not be resurrected as 'error'.
      // ABANDONED is the exception: discarded mid-POST, nothing to retry into.
      const permanent = err instanceof ApiError && isPermanentRefusal(err.status, err.code);
      const tx = (await db()).transaction(STORE, 'readwrite');
      const cur = (await tx.store.get(e.idempotencyKey)) as PendingEntry | undefined;
      if (cur !== undefined && isAbandoned(cur)) await tx.store.delete(e.idempotencyKey);
      else if (cur !== undefined && permanent) {
        await tx.store.put({
          ...cur,
          status: 'rejected' as const,
          rejectedReason: failureMessage(err, 'The server would not accept this reading.'),
        });
      } else if (cur !== undefined) await tx.store.put({ ...cur, status: 'error' as const });
      await tx.done;
      setSnapshot({ lastError: err instanceof Error ? err.message : 'Sync failed' });
    }
  }
  const remaining = await allEntries();
  if (!remaining.some((e) => isDrainableBy(e, userId))) setSnapshot({ lastError: null });
  await refreshCount();
  return { synced, remaining };
}

/**
 * Drain the queue (foreground sync). Passes are SERIALISED module-wide: two
 * concurrent callers (auto-drain effect, visibilitychange flush, Complete
 * button) never interleave sends of the same entry - the second caller waits,
 * then runs its own full pass, so its result reflects a pass that started
 * after it was requested.
 */
let drainChain: Promise<unknown> = Promise.resolve();
export function drain(store: TokenStore, userId?: string): Promise<DrainResult> {
  const run = drainChain.then(
    () => drainPass(store, userId),
    () => drainPass(store, userId),
  );
  drainChain = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

/**
 * Remove a queued entry (undo a mistaken tap before it syncs). 'removed' = it never
 * reached the server, so the caller must NOT issue a server delete (the record is
 * fully gone); 'inflight' = POST in progress, nothing deleted; 'absent' = not queued.
 */
export type DequeueResult = 'removed' | 'inflight' | 'absent' | 'not_yours';

export async function dequeueEntry(idempotencyKey: string, userId: string | undefined): Promise<DequeueResult> {
  const tx = (await db()).transaction(STORE, 'readwrite');
  const existing = (await tx.store.get(idempotencyKey)) as PendingEntry | undefined;
  const decision = removalDecision(existing, userId);
  if (decision === 'removable') await tx.store.delete(idempotencyKey);
  await tx.done;
  if (decision !== 'removable') return decision;
  await refreshCount();
  return 'removed';
}

/**
 * Drop every queued entry for a DISCARDED session — the queue's job, not the caller's, which
 * dequeued each key and ignored the one answer that mattered: an in-flight entry is REFUSED.
 * It then retried into a discarded encounter forever. What cannot be deleted is tombstoned;
 * the drain drops it when the POST settles, either way.
 */
export async function purgeEncounter(encounterId: string): Promise<{ removed: number; abandoned: number }> {
  const all = await allEntries();
  let removed = 0;
  let abandoned = 0;
  for (const e of all.filter((x) => x.encounterId === encounterId)) {
    const tx = (await db()).transaction(STORE, 'readwrite');
    const cur = (await tx.store.get(e.idempotencyKey)) as PendingEntry | undefined;
    if (cur === undefined) {
      await tx.done;
      continue;
    }
    if (cur.status === 'inflight') {
      await tx.store.put({ ...cur, abandoned: true });
      abandoned += 1;
    } else {
      await tx.store.delete(e.idempotencyKey);
      removed += 1;
    }
    await tx.done;
  }
  await refreshCount();
  return { removed, abandoned };
}

/** Initialise the in-memory count from IndexedDB (call once on app/session load). */
export async function hydrate(): Promise<void> {
  await refreshCount();
}
